import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'

import {
  Phone,
  DateRange,
  Place,
  Person,
  Email,
  Language,
  Public,
  Map,
  LocationCity,
  LocalPostOffice,
  Money,
  VerifiedUser,
  Circle,
  Numbers,
} from '@mui/icons-material'
import { InfoPlayer } from '@/views/components'
import { ApplicationState } from '@/state/root.models'

import { birthdayDate } from '@/utils'
import { format } from 'date-fns'
import { dateTimeFormat } from '@/constants/date'
import useDialog from '@/hooks/ui/use-dialog.hook'
import { DialogsEnum } from '@/enums'

import { AccountKYCStatusData } from '@/constants/player-card'
import { DialogKYCStatusForm } from '../dialogs'

export function PersonalInfo() {
  const { t } = useTranslation()

  const { handleDialogAction } = useDialog()

  const { playerCard } = useSelector(
    ({ playerCard }: ApplicationState) => playerCard,
  )

  const infoArray = [
    {
      icon: <Person />,
      title: t('labels.id'),
      data: playerCard?.id,
    },
    {
      icon: <VerifiedUser />,
      title: t('playerCard.kycStatus'),
      data: (
        <>
          {playerCard?.kycStatus && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 10,
                gap: 0.5,
              }}
            >
              <Circle
                className={AccountKYCStatusData[playerCard?.kycStatus].color}
                fontSize="inherit"
              />
              <Typography variant="caption" component="p" lineHeight="1rem">
                {t(`${AccountKYCStatusData[playerCard?.kycStatus].label}`)}
              </Typography>
            </Box>
          )}
        </>
      ),
      click: () =>
        handleDialogAction({
          open: true,
          id: DialogsEnum.DIALOG_EDIT_KYC_STATUS,
        }),
    },
    {
      icon: <Email />,
      title: t('labels.email'),
      data: playerCard?.email,
    },
    {
      icon: <Phone />,
      title: t('labels.mobileNumber'),
      data: playerCard?.fullMobileNumber,
    },
    {
      icon: <DateRange />,
      title: t('labels.birthday'),
      data: playerCard?.dateOfBirth
        ? birthdayDate(playerCard?.dateOfBirth, t('labels.yearsText'))
        : '-',
    },
    {
      icon: <Language />,
      title: t('labels.language'),
      data: playerCard?.language?.toUpperCase(),
      sxProps: { width: '48.2%' },
    },
    {
      icon: <Person />,
      title: t('labels.gender'),
      data: playerCard?.gender,
      sxProps: { width: '48.2%' },
    },
    {
      icon: <Public />,
      title: t('labels.country'),
      data: playerCard?.country,
      sxProps: { width: '48.2%' },
    },
    {
      icon: <Map />,
      title: t('labels.state'),
      data: playerCard?.state,
      sxProps: { width: '48.2%' },
    },
    {
      icon: <LocationCity />,
      title: t('labels.city'),
      data: playerCard?.city,
      sxProps: { width: '48.2%' },
    },
    {
      icon: <LocalPostOffice />,
      title: t('labels.zipCode'),
      data: playerCard?.zipCode,
      sxProps: { width: '48.2%' },
    },
    {
      icon: <Place />,
      title: t('labels.address'),
      data: `${playerCard?.city}, ${playerCard?.state}, ${playerCard?.country}`,
    },
    {
      icon: <Money />,
      title: t('labels.currency'),
      data: playerCard?.currency,
    },
    {
      icon: <Numbers />,
      title: t('labels.affiliateCode'),
      data: playerCard?.affiliateId || '-',
    },
    {
      icon: <DateRange />,
      title: t('labels.registrationDate'),
      data: playerCard?.registrationDate
        ? format(new Date(playerCard.registrationDate), dateTimeFormat)
        : '-',
    },
  ]

  return (
    <>
      <Box mt={2}>
        <Typography fontWeight={700} paragraph>
          {t('playerCard.personalInformation')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          {infoArray.map((item) => (
            <InfoPlayer key={item.title} {...item} />
          ))}
        </Box>
      </Box>
      <DialogKYCStatusForm />
    </>
  )
}
